import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { api, formatErr } from "@/lib/api";
import { fmtAmount, fmtDate as formatDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { InvoiceFields, invoicePayload } from "@/components/InvoiceFields";
import { FilePicker } from "@/components/PaymentFiles";

const STEP = { weekly: [0, 7], monthly: [1, 0], "bi-monthly": [2, 0], quarterly: [3, 0], "half-yearly": [6, 0], yearly: [12, 0] };
const missedPeriods = (o) => {
  if (!o?.next_due_date || o.compliance_key) return 0;
  const step = o.frequency === "custom" ? [0, Number(o.custom_days) || 0] : STEP[o.frequency];
  if (!step || (!step[0] && !step[1])) return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const base = new Date(`${o.next_due_date}T00:00:00`);
  let n = 0;
  for (let i = 1; i < 500; i += 1) {
    const d = new Date(base.getFullYear(), base.getMonth() + step[0] * i, base.getDate() + step[1] * i);
    if (d > today) break;
    n += 1;
  }
  return n;
};

export default function MarkPaidDialog({ open, onOpenChange, obligation, onDone, hideInvoice = false }) {
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState({ billable: null, amount: "" });
  const [files, setFiles] = useState([]);
  const missed = missedPeriods(obligation);
  const isFiling = !!obligation?.compliance_key;

  useEffect(() => {
    if (open && obligation) {
      setPaymentDate(new Date());
      setAmount(obligation.is_variable_amount ? "" : (obligation.amount != null ? String(obligation.amount) : ""));
      setNotes("");
      setFiles([]);
      setInvoice({ billable: null, amount: "" });
      if (obligation.compliance_key) {
        api.get(`/compliance/fee-preset/${obligation.id}`)
          .then((r) => r.data.fee != null && setInvoice((v) => ({ ...v, amount: v.amount || String(r.data.fee) }))).catch(() => {});
      }
    }
  }, [open, obligation]);

  if (!obligation) return null;

  const submit = async () => {
    if (!isFiling && obligation.is_variable_amount && !amount) {
      toast.error("This obligation has variable amount — please enter the amount paid");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        payment_date: format(paymentDate, "yyyy-MM-dd"),
        notes: notes || null,
        ...(isFiling && !hideInvoice && invoicePayload(invoice)),
      };
      if (amount !== "") payload.amount = parseFloat(amount);
      const { data } = await api.post(`/obligations/${obligation.id}/mark-paid`, payload);
      let failed = 0;
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file); fd.append("payment_id", data.last_payment_id);
        try { await api.post(`/obligations/${obligation.id}/attachments`, fd); } catch (e) { failed += 1; toast.error(`${file.name}: ${formatErr(e)}`); }
      }
      const saved = files.length - failed;
      toast.success((isFiling ? "Marked done — next period's reminder is set" : "Payment recorded — next due date advanced") + (saved ? ` · ${saved} document${saved > 1 ? "s" : ""} attached` : ""));
      onDone?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mark-paid-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {isFiling ? "Mark done" : "Mark paid"}</DialogTitle>
          <DialogDescription>
            <b>{obligation.name}</b> · {obligation.category}
            <br />
            Currently due: <span className="mono">{obligation.next_due_date}</span>
            {!obligation.is_variable_amount && obligation.amount != null && (
              <>
                <br />
                Suggested amount: <span className="mono">{fmtAmount(obligation.amount, obligation.currency)}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">{isFiling ? "Filed on" : "Payment date"}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button data-testid="markpaid-date" type="button" variant="outline" className="w-full justify-start mono font-normal mt-1">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {format(paymentDate, "dd MMM yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={paymentDate} onSelect={(d) => d && setPaymentDate(d)} initialFocus captionLayout="dropdown-buttons" fromYear={2000} toYear={new Date().getFullYear() + 2} defaultMonth={paymentDate} />
              </PopoverContent>
            </Popover>
          </div>
          {isFiling ? (hideInvoice ? null : <InvoiceFields value={invoice} onChange={setInvoice} testid="markpaid-invoice" />) : (
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Amount paid {obligation.is_variable_amount && <span className="normal-case tracking-normal text-amber-700">· variable</span>}
            </Label>
            <Input
              data-testid="markpaid-amount"
              type="number"
              min="0"
              step="0.01"
              className="mt-1 mono"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={obligation.is_variable_amount ? "Enter the amount you actually paid" : "Leave blank to use suggested"}
            />
          </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea data-testid="markpaid-notes" rows={2} className="mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional — receipt number, transaction ID, etc." />
          </div>
          {missed > 0 && (
            <div data-testid="markpaid-missed-warning" className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2.5">
              This records the {formatDate(obligation.next_due_date)} period.{" "}
              {obligation.payment_mode === "auto"
                ? `${missed} later missed period${missed > 1 ? "s" : ""} up to today will be skipped (auto debit) and the next due date will move past today.`
                : `${missed} more missed period${missed > 1 ? "s" : ""} will stay overdue. Record each one separately.`}
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">{isFiling ? "Acknowledgement / challan" : "Receipt"} <span className="normal-case tracking-normal text-slate-400">· optional, up to 15 MB each</span></Label>
            <div className="mt-1"><FilePicker files={files} onChange={setFiles} testid="markpaid-files" /></div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button data-testid="markpaid-cancel" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="markpaid-submit" disabled={saving} onClick={submit} className="bg-blue-700 hover:bg-blue-800 text-white">
            {saving ? "Recording…" : isFiling ? "Mark done" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
